import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@finora/api-client";
import { Button } from "@finora/ui";
import { api } from "../../lib/api";
import { ModuleGateNotice } from "../../components/ModuleGateNotice";

export function RaiffeisenCardTab() {
  const queryClient = useQueryClient();
  const {
    data: settings,
    isLoading,
    isError: isSettingsError,
    error: settingsError,
  } = useQuery({
    queryKey: ["raiffeisen-card-settings"],
    queryFn: () => api.getRaiffeisenCardSettings(),
    // Isti razlog kao OtpCardTab/IntesaCardTab - ako modul "raiffeisen_card"
    // nije aktivan za firmu (RequireModule guard na backendu vraća 403),
    // nema smisla ponavljati zahtev.
    retry: false,
  });

  const [enabled, setEnabled] = useState(false);
  const [environment, setEnvironment] = useState<"TEST" | "PRODUCTION">("TEST");
  const [merchantId, setMerchantId] = useState("");
  const [terminalId, setTerminalId] = useState("");
  const [currency, setCurrency] = useState("941");
  const [privateKeyPem, setPrivateKeyPem] = useState("");
  const [bankCertPem, setBankCertPem] = useState("");

  useEffect(() => {
    if (settings) {
      setEnabled(settings.raiffeisenCardEnabled);
      setEnvironment(settings.raiffeisenCardEnvironment);
      setMerchantId(settings.raiffeisenCardMerchantId ?? "");
      setTerminalId(settings.raiffeisenCardTerminalId ?? "");
      setCurrency(settings.raiffeisenCardCurrency);
    }
  }, [settings]);

  const save = useMutation({
    mutationFn: () =>
      api.updateRaiffeisenCardSettings({
        raiffeisenCardEnabled: enabled,
        raiffeisenCardEnvironment: environment,
        raiffeisenCardMerchantId: merchantId || undefined,
        raiffeisenCardTerminalId: terminalId || undefined,
        raiffeisenCardCurrency: currency || undefined,
        raiffeisenCardPrivateKeyPem: privateKeyPem || undefined,
        raiffeisenCardBankCertPem: bankCertPem || undefined,
      }),
    onSuccess: (updated) => {
      queryClient.setQueryData(["raiffeisen-card-settings"], updated);
      setPrivateKeyPem("");
      setBankCertPem("");
    },
  });

  if (isSettingsError) {
    if (settingsError instanceof ApiError && settingsError.response.errorCode === "MODULE_NOT_AVAILABLE") {
      return <ModuleGateNotice error={settingsError} moduleKey="raiffeisen_card" />;
    }
    return (
      <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
        {settingsError instanceof Error ? settingsError.message : "Učitavanje nije uspelo."}
      </p>
    );
  }

  if (isLoading || !settings) {
    return <p className="text-sm text-slate-500">Učitavanje...</p>;
  }

  return (
    <div className="max-w-xl space-y-5">
      <div>
        <h2 className="text-sm font-semibold text-slate-800">Kartice - Raiffeisen banka</h2>
        <p className="text-xs text-slate-400">
          Naplata faktura platnim karticama preko Raiffeisen banke (UPC "e-Commerce Connect
          Gateway"). Kupac se preusmerava na hostovanu stranicu banke (unos podataka kartice, 3D
          Secure), pa se vraća nazad na fakturu sa ishodom. Merchant ID i Terminal ID dobijaš uz
          E-commerce ugovor; RSA privatni ključ generišeš sam (alat koji dobijaš od banke),
          njegov .crt se šalje banci na ec@upc.ua, a bankin sertifikat (test-server.cert) dobijaš
          od banke nazad - njega unosi ovde kao "Bankin sertifikat".
        </p>
        <p className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700">
          VAŽNO - ovo HAFiskal NE radi automatski: za model predautorizacije (obavezan ako prodaješ
          fizičku robu koja se isporučuje kasnije), realizaciju (slanje na konačnu naplatu)
          rezervisanog iznosa mora RUČNO potvrditi osoba iz firme, prijavom na Raiffeisen
          Trgovački portal (link dobijen od banke) i klikom na "Potvrdi" za svaku transakciju - u
          roku od 7-30 dana od plaćanja. Banka ne nudi API za ovaj korak.
        </p>
      </div>

      <label className="flex items-center gap-2 text-sm text-slate-700">
        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
        Aktivna naplata karticama (Raiffeisen banka) za ovu firmu
      </label>

      <div>
        <label className="block text-xs font-medium text-slate-500">Okruženje</label>
        <select
          value={environment}
          onChange={(e) => setEnvironment(e.target.value as "TEST" | "PRODUCTION")}
          className="mt-1 h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
        >
          <option value="TEST">Test (ecg.test.upc.ua)</option>
          <option value="PRODUCTION">Produkcija</option>
        </select>
        <p className="mt-1 text-xs text-slate-400">
          Produkcioni domen gateway-a nije još potvrđen sa bankom - uključivanje produkcije je
          privremeno blokirano dok se ne dobije tačan URL od Raiffeisen/UPC (ec@upc.ua).
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-slate-500">Merchant ID (MID)</label>
          <input
            value={merchantId}
            onChange={(e) => setMerchantId(e.target.value)}
            placeholder="npr. 1770000"
            className="mt-1 h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-500">Terminal ID (TID)</label>
          <input
            value={terminalId}
            onChange={(e) => setTerminalId(e.target.value)}
            placeholder="npr. E7000000"
            className="mt-1 h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
          />
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-slate-500">Šifra valute</label>
        <input
          value={currency}
          onChange={(e) => setCurrency(e.target.value)}
          placeholder="941 (RSD)"
          className="mt-1 h-10 w-40 rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-slate-500">RSA privatni ključ (.pem)</label>
        <textarea
          value={privateKeyPem}
          onChange={(e) => setPrivateKeyPem(e.target.value)}
          rows={6}
          placeholder={
            settings.hasRaiffeisenCardPrivateKey
              ? "•••••••••• (nepromenjeno - unet je ključ)"
              : "-----BEGIN PRIVATE KEY-----\n..."
          }
          className="mt-1 w-full rounded-lg border border-border px-3 py-2 font-mono text-xs focus:border-brand focus:outline-none"
        />
        {settings.hasRaiffeisenCardPrivateKey && (
          <p className="mt-1 text-xs text-slate-400">Trenutno je unet ključ - ostavi prazno da ga zadržiš.</p>
        )}
      </div>

      <div>
        <label className="block text-xs font-medium text-slate-500">Bankin sertifikat (test-server.cert)</label>
        <textarea
          value={bankCertPem}
          onChange={(e) => setBankCertPem(e.target.value)}
          rows={6}
          placeholder={
            settings.hasRaiffeisenCardBankCert
              ? "•••••••••• (nepromenjeno - unet je sertifikat)"
              : "-----BEGIN CERTIFICATE-----\n..."
          }
          className="mt-1 w-full rounded-lg border border-border px-3 py-2 font-mono text-xs focus:border-brand focus:outline-none"
        />
        {settings.hasRaiffeisenCardBankCert && (
          <p className="mt-1 text-xs text-slate-400">Trenutno je unet sertifikat - ostavi prazno da ga zadržiš.</p>
        )}
      </div>

      {save.isError && save.error instanceof ApiError && save.error.response.errorCode === "MODULE_NOT_AVAILABLE" ? (
        <ModuleGateNotice error={save.error} moduleKey="raiffeisen_card" />
      ) : (
        save.isError && (
          <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
            {save.error instanceof Error ? save.error.message : "Čuvanje nije uspelo."}
          </p>
        )
      )}
      {save.isSuccess && !save.isPending && (
        <p className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700">Sačuvano.</p>
      )}

      <Button disabled={save.isPending} onClick={() => save.mutate()}>
        Sačuvaj
      </Button>
    </div>
  );
}
